import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceHeatmap = ({ companies, selectedMetric }) => {
  const getHeatmapValue = (company) => {
    switch (selectedMetric) {
      case 'sales':
        return company.salesGrowth;
      case 'collections':
        return company.collectionRate;
      case 'growth':
        return company.targetAchievement;
      default:
        return company.salesGrowth;
    }
  };

  const getHeatmapColor = (value, metric) => {
    let intensity;
    
    switch (metric) {
      case 'sales':
        intensity = Math.max(0, Math.min(100, (value + 20) * 2.5));
        break;
      case 'collections':
        intensity = Math.max(0, Math.min(100, value));
        break;
      case 'growth':
        intensity = Math.max(0, Math.min(100, value));
        break;
      default:
        intensity = Math.max(0, Math.min(100, (value + 20) * 2.5));
    }

    if (intensity >= 80) return 'bg-success/80';
    if (intensity >= 60) return 'bg-success/60';
    if (intensity >= 40) return 'bg-warning/60';
    if (intensity >= 20) return 'bg-warning/40';
    return 'bg-error/40';
  };

  const getMetricLabel = () => {
    switch (selectedMetric) {
      case 'sales':
        return 'Sales Growth';
      case 'collections':
        return 'Collection Rate';
      case 'growth':
        return 'Target Achievement';
      default:
        return 'Sales Growth';
    }
  };

  const formatValue = (value, metric) => {
    switch (metric) {
      case 'sales':
        return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
      case 'collections':
        return `${value.toFixed(1)}%`;
      case 'growth':
        return `${value.toFixed(1)}%`;
      default:
        return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">Performance Heatmap</h3>
        <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
      </div>

      <div className="mb-3">
        <div className="text-xs text-muted-foreground mb-2">{getMetricLabel()}</div>
        
        {/* Legend */}
        <div className="flex items-center space-x-2 mb-4">
          <span className="text-xs text-muted-foreground">Low</span>
          <div className="flex space-x-1">
            <div className="w-3 h-3 bg-error/40 rounded-sm"></div>
            <div className="w-3 h-3 bg-warning/40 rounded-sm"></div>
            <div className="w-3 h-3 bg-warning/60 rounded-sm"></div>
            <div className="w-3 h-3 bg-success/60 rounded-sm"></div>
            <div className="w-3 h-3 bg-success/80 rounded-sm"></div>
          </div>
          <span className="text-xs text-muted-foreground">High</span>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="grid grid-cols-2 gap-2">
        {companies.slice(0, 8).map((company) => {
          const value = getHeatmapValue(company);
          const colorClass = getHeatmapColor(value, selectedMetric);
          
          return (
            <div
              key={company.id}
              className={`${colorClass} rounded-lg p-3 border border-border/50 hover:border-border transition-colors duration-150`}
            >
              <div className="text-xs font-medium text-foreground mb-1 truncate">
                {company.name}
              </div>
              <div className="text-sm font-semibold text-foreground">
                {formatValue(value, selectedMetric)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <span className="text-muted-foreground">Best Performer:</span>
            <div className="font-medium text-foreground mt-1">
              {companies.reduce((best, company) => 
                getHeatmapValue(company) > getHeatmapValue(best) ? company : best
              ).name}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">Needs Attention:</span>
            <div className="font-medium text-foreground mt-1">
              {companies.reduce((worst, company) => 
                getHeatmapValue(company) < getHeatmapValue(worst) ? company : worst
              ).name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceHeatmap;